/**
 * Query-key factory (+ shared wire types) for the per-app native-tool
 * allowlist picker (FRONT-1385, `ToolPickerDialog` / `CapabilitiesSection`'s
 * "Eddy tools" card). `GET /api/admin/apps/:slug/tool-access`
 * (`src/admin/apps-routes.ts`) takes no search parameter at all — it reads
 * only `orgId` and `slug`, and always returns the app's full offerable
 * catalogue plus the owner's stored selection.
 *
 * The search term is therefore NOT part of the query key. FRONT-1501's rule
 * is that a list hook's key must contain every input that changes the
 * *request* — and here the term changes nothing about the request, because
 * filtering happens entirely client-side over data already in hand. Folding
 * it into the key anyway (an earlier version of this file did) bought
 * nothing and cost a redundant round-trip plus a fresh cache entry per
 * keystroke-term, all resolving to an identical response. Measured in a
 * real browser: typing 3 distinct search terms issued 4 requests to this
 * endpoint, every one returning the identical full catalogue.
 *
 * The thing that CAN silently go inert here is the client-side filter, not
 * the key. That filter and its grouping now live in `tool-picker-model.ts`,
 * re-exported here so imports don't need to change.
 */
export { filterOfferableTools } from "./tool-picker-model";

/** Trim + lowercase, once, so the filter sees a consistent value. */
export function normalizeToolSearch(search?: string | null): string {
  return (search ?? "").trim().toLowerCase();
}

export const toolAccessKeys = {
  all: ["admin-app-tool-access"] as const,
  /** The invalidation target — also the only list, since there's no search-scoped variant. */
  lists: (slug: string) => [...toolAccessKeys.all, "list", slug] as const,
  /** The one list for this app. No search argument: see file header. */
  list: (slug: string) => [...toolAccessKeys.lists(slug)] as const,
} as const;

/** One entry from the live offerable catalogue (`tool-access`'s `tools`). */
export interface OfferableAppTool {
  name: string;
  description: string;
  parameters?: unknown;
  // Short summary, computed server-side. Optional only for hand-built fixtures.
  summary?: string;
  // FRONT-2971: set on a tool from one of the workspace's MCP servers.
  server?: string;
  // FRONT-2971: a saved MCP pick whose server has not answered yet. Kept, never stale.
  unverified?: true;
}

/**
 * A stored allowlist entry that no longer grants access (D6, FRONT-1385) —
 * the live registry is the authority, so a stale entry is reported to the
 * owner rather than silently pruned. `unknown`: the tool no longer exists at
 * all. `not_offerable`: it still exists but has fallen outside the app-door
 * allowlist (`src/apps/tool-offer.ts`).
 */
export interface StaleAppTool {
  name: string;
  reason: "unknown" | "not_offerable";
}

/** `GET /api/admin/apps/:slug/tool-access` response shape. */
export interface ToolAccessResponse {
  tools: OfferableAppTool[];
  selected: string[];
  stale: StaleAppTool[];
  // FRONT-2971: org MCP servers the owner must connect before their tools show here.
  mcpNeedsConnect?: string[];
  scopeGranted: boolean;
}
